import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "../../styles/header.css";

// Use the transparent logo.png provided alongside this file, and update the path

const services = [
  {
    title: "Managed IT",
    text: "Proactive monitoring and care",
    to: "/services/managed-it",
    icon: (
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M8 20h8M12 16v4" />
      </>
    ),
  },
  {
    title: "IT Support",
    text: "Fast help when you need it",
    to: "/services/it-support",
    icon: (
      <>
        <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
        <rect x="3" y="14" width="4" height="6" rx="1.5" />
        <rect x="17" y="14" width="4" height="6" rx="1.5" />
      </>
    ),
  },
  {
    title: "IT Consultancy",
    text: "Strategy and roadmaps",
    to: "/services/it-consultancy",
    icon: (
      <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" />
    ),
  },
  {
    title: "Cloud Computing",
    text: "Migration, hosting and scale",
    to: "/services/cloud-computing",
    icon: (
      <path d="M7 18a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8.5 4.8 4.8 0 0 1 17 18z" />
    ),
  },
  {
    title: "Cyber Security",
    text: "Protect your data and teams",
    to: "/services/cyber-security",
    icon: (
      <>
        <path d="M12 3l8 3v6c0 4.5-3.3 8-8 9-4.7-1-8-4.5-8-9V6z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
];

const links = [
  { label: "Home", to: "/", end: true },
  { label: "About us", to: "/about-us" },
  { label: "IT solutions", dropdown: true },
  { label: "Blogs", to: "/blogs" },
  { label: "Careers", to: "/careers" },
  { label: "FAQs", to: "/faqs" },
  { label: "Contact Us", to: "/contact-us" },
];

const socials = [
  {
    name: "X",
    href: "#",
    icon: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    ),
  },
  {
    name: "Facebook",
    href: "#",
    icon: (
      <path d="M13.5 22v-8.2h2.8l.5-3.4h-3.3V8.3c0-1 .4-1.8 1.9-1.8h1.5V3.4c-.3 0-1.3-.1-2.4-.1-2.5 0-4.2 1.5-4.2 4.3v2.8H7.5v3.4h2.8V22z" />
    ),
  },
  {
    name: "Instagram",
    href: "#",
    icon: (
      <>
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle
          cx="12"
          cy="12"
          r="4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle cx="17.3" cy="6.7" r="1.2" />
      </>
    ),
  },
  {
    name: "LinkedIn",
    href: "#",
    icon: (
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.5h4V21H3zm6.5 0h3.8v1.6h.1c.5-1 1.8-1.9 3.7-1.9 4 0 4.7 2.6 4.7 6V21h-4v-5.1c0-1.2 0-2.8-1.7-2.8s-2 1.3-2 2.7V21h-4z" />
    ),
  },
];

const Chevron = () => (
  <svg
    className="gt9-chev"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Arrow = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false); // desktop dropdown
  const [mobileDropOpen, setMobileDropOpen] = useState(false); // drawer accordion

  const dropRef = useRef(null);
  const burgerRef = useRef(null);
  const closeRef = useRef(null);
  const { pathname } = useLocation();

  // shrink + shadow on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close everything after navigating
  useEffect(() => {
    setDrawerOpen(false);
    setDropOpen(false);
  }, [pathname]);

  // lock page scroll while the drawer is open
  useEffect(() => {
    document.body.classList.toggle("gt9-lock", drawerOpen);
    if (drawerOpen) closeRef.current?.focus();
    return () => document.body.classList.remove("gt9-lock");
  }, [drawerOpen]);

  // Esc, outside click and resize
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDropOpen(false);
        setDrawerOpen(false);
      }
    };
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setDropOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth > 1020) setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    burgerRef.current?.focus();
  };

  return (
    <header className="gt9-header">
      {/* Main sticky bar */}
      <div className={`gt9-bar${scrolled ? " is-scrolled" : ""}`}>
        <div className="gt9-container">
          <Link to="/" className="gt9-logo" aria-label="Global Tech9 LLC home">
            <img src="/logo.png" alt="Global Tech9 LLC" />
          </Link>

          <nav aria-label="Main">
            <ul className="gt9-nav">
              {links.map((l) =>
                l.dropdown ? (
                  <li
                    key={l.label}
                    ref={dropRef}
                    className={`has-drop${dropOpen ? " open" : ""}`}
                  >
                    <button
                      type="button"
                      aria-expanded={dropOpen}
                      aria-haspopup="true"
                      onClick={() => setDropOpen((o) => !o)}
                    >
                      {l.label}
                      <Chevron />
                    </button>
                    <ul className="gt9-drop">
                      {services.map((s) => (
                        <li key={s.title}>
                          <Link to={s.to}>
                            <i>
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                {s.icon}
                              </svg>
                            </i>
                            <span>
                              <b>{s.title}</b>
                              <small>{s.text}</small>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li key={l.label}>
                    <NavLink to={l.to} end={l.end}>
                      {l.label}
                    </NavLink>
                  </li>
                ),
              )}
            </ul>
          </nav>

          <Link to="/contact-us" className="gt9-cta-header">
            Get a quote
            <Arrow />
          </Link>

          <button
            type="button"
            ref={burgerRef}
            className="gt9-burger"
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={drawerOpen}
            aria-controls="gt9-drawer"
            onClick={() => setDrawerOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`gt9-overlay${drawerOpen ? " show" : ""}`}
        onClick={closeDrawer}
      />
      <aside
        id="gt9-drawer"
        className={`gt9-drawer${drawerOpen ? " show" : ""}`}
        aria-label="Mobile menu"
      >
        <div className="gt9-drawer__head">
          <strong>Menu</strong>
          <button
            type="button"
            ref={closeRef}
            className="gt9-close"
            aria-label="Close menu"
            onClick={closeDrawer}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <ul className="gt9-mnav">
          {links.map((l) =>
            l.dropdown ? (
              <li key={l.label} className={mobileDropOpen ? "open" : ""}>
                <button
                  type="button"
                  aria-expanded={mobileDropOpen}
                  onClick={() => setMobileDropOpen((o) => !o)}
                >
                  {l.label}
                  <Chevron />
                </button>
                <ul className="gt9-msub">
                  {services.map((s) => (
                    <li key={s.title}>
                      <Link to={s.to}>{s.title}</Link>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={l.label}>
                <NavLink to={l.to} end={l.end}>
                  {l.label}
                </NavLink>
              </li>
            ),
          )}
        </ul>

        <Link to="/contact-us" className="gt9-cta-header">
          Get a quote
          <Arrow />
        </Link>

        <ul className="gt9-social">
          {socials.map((s) => (
            <li key={s.name}>
              <a
                href={s.href}
                aria-label={s.name}
                target="_blank"
                rel="noreferrer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  {s.icon}
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </aside>
    </header>
  );
};

export default Header;
